import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import { notification } from "antd";
import {
  CompanyInput,
  CompanyLeadership,
  CompanyLocation,
  CompanyName,
} from "@lhd/types";
import { getCompanyId } from "@lhd/utility";

export interface CompanyFilterOptionsParams {
  target_tab?: string;
  field_key?: string;
  active_filters?: Record<string, string[]>;
  active_search?: Record<string, string>;
  filter_order?: string[];
  scope?: string | null;
}

export const useFetchCompanyFilterOptions = (
  params: CompanyFilterOptionsParams | null,
  enabled?: boolean,
) => {
  return useQuery(
    [
      QUERY_NAME.COMPANY,
      "filter_options",
      params?.target_tab,
      params?.field_key,
      params?.active_filters,
      params?.active_search,
      params?.filter_order,
      params?.scope,
    ],
    async () => {
      if (!params) return null;

      const res = await axiosInstanceV2({
        method: "post",
        url: `${SERVICES.COMPANIES}/filter_options`,
        data: {
          ...params,
        },
      });

      return res.data;
    },
    {
      enabled: !!params?.target_tab && !!params?.field_key && (enabled ?? true),
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useSubmitCompanyV2 = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, companyId, method, headers }: any) =>
      axiosInstanceV2({
        method: method ? method : "patch",
        url: `${SERVICES.COMPANIES}/${companyId}`,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useFetchOneCompanyV2 = (companyId: any) => {
  return useQuery(
    [QUERY_NAME.COMPANY, companyId],
    () =>
      axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANIES}/${companyId}`,
      }).then((res) => res.data),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!companyId,
    },
  );
};

// Fetch company leaderships (GET)
export const useFetchCompanyLeaderships = (
  company_id: number,
  page = 1,
  per_page = 25,
  offset = 0,
) => {
  const queryResult = useQuery(
    [QUERY_NAME.COMPANY_LEADERSHIPS, company_id, page, per_page, offset],
    () =>
      axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANY_LEADERSHIPS}`,
        params: {
          company_id,
          page,
          per_page,
          offset,
        },
      }).then((res) => {
        return {
          data: res.data as CompanyLeadership[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      enabled: !!company_id,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );

  return {
    ...queryResult,
    isLoading: company_id ? queryResult.isLoading : false,
    data: queryResult.data
      ? {
          ...queryResult.data,
          meta: {
            ...queryResult.data.meta,
            dataUpdatedAt: queryResult.dataUpdatedAt,
          },
        }
      : undefined,
  };
};

// Create company leadership (POST)
export const useCreateCompanyLeadership = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload, headers }: any) =>
      axiosInstanceV2({
        method: "post",
        url: `${SERVICES.COMPANY_LEADERSHIPS}`,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_LEADERSHIPS]);
        notification["success"]({ message: "役員を作成しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        onError && onError(error);
      },
    },
  );
};

// Update company leadership (PATCH)
export const useUpdateCompanyLeadership = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload, headers }: any) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.COMPANY_LEADERSHIPS}/${id}`,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_LEADERSHIPS]);
        notification["success"]({ message: "役員を更新しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        onError && onError(error);
      },
    },
  );
};

// Delete company leadership (DELETE)
export const useDeleteCompanyLeadership = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, company_id, headers }: any) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.COMPANY_LEADERSHIPS}/${id}`,
        params: { company_id },
        headers,
      }),
    {
      onSuccess() {
        notification["success"]({ message: "役員を削除しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_LEADERSHIPS]);
        onError && onError(error);
      },
    },
  );
};

export const useFetchCompanyNames = (
  company_id: number,
  page = 1,
  per_page = 25,
  offset = 0,
) => {
  const queryResult = useQuery(
    [QUERY_NAME.COMPANY_NAMES, company_id, page, per_page, offset],
    () =>
      axiosInstanceV2({
        method: "get",
        url: SERVICES.COMPANY_NAMES,
        params: {
          company_id,
          page,
          per_page,
          offset,
        },
      }).then((res) => {
        return {
          data: res.data as CompanyName[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      enabled: !!company_id,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );

  return {
    ...queryResult,
    isLoading: company_id ? queryResult.isLoading : false,
    data: queryResult.data
      ? {
          ...queryResult.data,
          meta: {
            ...queryResult.data.meta,
            dataUpdatedAt: queryResult.dataUpdatedAt,
          },
        }
      : undefined,
  };
};
// Create company name (POST)
export const useCreateCompanyName = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload, headers }: any) =>
      axiosInstanceV2({
        method: "post",
        url: SERVICES.COMPANY_NAMES,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_NAMES]);
        notification["success"]({ message: "事業所名を作成しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        onError && onError(error);
      },
    },
  );
};

// Update company name (PATCH)
export const useUpdateCompanyName = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload, headers }: any) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.COMPANY_NAMES}/${id}`,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_NAMES]);
        notification["success"]({ message: "事業所名を更新しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        onError && onError(error);
      },
    },
  );
};

// Delete company name (DELETE)
export const useDeleteCompanyName = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, company_id, headers }: any) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.COMPANY_NAMES}/${id}`,
        params: { company_id },
        headers,
      }),
    {
      onSuccess() {
        notification["success"]({ message: "事業所名を削除しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_NAMES]);
        onError && onError(error);
      },
    },
  );
};

// Fetch company locations (GET)
export const useFetchCompanyLocations = (
  company_id: number,
  page = 1,
  per_page = 25,
  tabParams: string,
) => {
  const queryResult = useQuery(
    [QUERY_NAME.COMPANY_LOCATIONS, company_id, page, per_page, tabParams],
    () =>
      axiosInstanceV2({
        method: "get",
        url: SERVICES.COMPANY_LOCATIONS,
        params: {
          company_id,
          page,
          per_page,
          tabParams,
        },
      }).then((res) => {
        return {
          data: res.data as CompanyLocation[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      enabled: !!company_id,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  return {
    ...queryResult,
    isLoading: company_id ? queryResult.isLoading : false,
    data: queryResult.data
      ? {
          ...queryResult.data,
          meta: {
            ...queryResult.data.meta,
            dataUpdatedAt: queryResult.dataUpdatedAt,
          },
        }
      : undefined,
  };
};

// Create company location (POST)
export const useCreateCompanyLocation = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload, headers }: any) =>
      axiosInstanceV2({
        method: "post",
        url: SERVICES.COMPANY_LOCATIONS,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_LOCATIONS]);
        notification["success"]({ message: "拠点を作成しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        onError && onError(error);
      },
    },
  );
};

// Update company location (PATCH)
export const useUpdateCompanyLocation = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload, headers }: any) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.COMPANY_LOCATIONS}/${id}`,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_LOCATIONS]);
        notification["success"]({ message: "拠点を更新しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        onError && onError(error);
      },
    },
  );
};

// Delete company location (DELETE)
export const useDeleteCompanyLocation = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, company_id, headers }: any) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.COMPANY_LOCATIONS}/${id}`,
        params: { company_id },
        headers,
      }),
    {
      onSuccess() {
        notification["success"]({ message: "拠点を削除しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_LOCATIONS]);
        onError && onError(error);
      },
    },
  );
};

export const useSubmitNewCompanyV2 = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload }: { payload: CompanyInput[] }) =>
      axiosInstanceV2({
        method: "POST",
        url: `${SERVICES.COMPANIES}/save`,
        data: {
          current_login_company_id: getCompanyId(),
          companies_data: payload,
        },
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY]);
      },
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.errors?.[0]?.message,
        });
      },
    },
  );
};

export const useSubmitPaymentNotRequiredCompany = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ value }: { value: boolean }) =>
      axiosInstanceV2({
        method: "PATCH",
        url: `${SERVICES.COMPANIES}/${id}/payment_not_required`,
        data: {
          value,
        },
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANIES + "detail"]);
      },
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.errors?.msg,
        });
      },
    },
  );
};
